import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  BrandLiveOverlay,
  getBrandLiveOverlay,
  sendBrandLiveBeat,
} from '../../api/payment/brand-live.api';
import BrandLiveCard from '../../components/brand-live/brand-live-card';
import { nextRotationIndex, shouldBeat } from '../../utils/brand-live';

/**
 * Overlay QUẢNG CÁO LIVESTREAM — chèn vào OBS bằng Browser Source, link có `?key=`.
 *
 * Không đăng nhập. Tải danh sách quảng cáo khi mở và mỗi 60 giây, xoay thẻ theo `rotateSeconds`.
 * Giờ chỉ được tính khi chạy trong OBS (có `window.obsstudio`), đang phát sóng và nguồn đang hiện:
 * mỗi 60 giây gửi một nhịp, máy chủ trả lại danh sách mới.
 */

interface ObsStudio {
  getStatus?: (cb: (s: { streaming: boolean }) => void) => void;
}

declare global {
  interface Window {
    obsstudio?: ObsStudio;
  }
}

const TICK_MS = 60000;

const POSITION: Record<string, React.CSSProperties> = {
  Corner: { position: 'fixed', right: 24, bottom: 24 },
  BottomBar: { position: 'fixed', left: 0, right: 0, bottom: 0 },
  Vertical: { position: 'fixed', right: 24, top: 24 },
};

const PageAdLivestream: React.FC = () => {
  const { slotId = '' } = useParams<{ slotId: string }>();
  const [searchParams] = useSearchParams();
  const key = searchParams.get('key') || '';

  const [overlay, setOverlay] = useState<BrandLiveOverlay | null>(null);
  const [index, setIndex] = useState(0);
  const present = typeof window !== 'undefined' && !!window.obsstudio;

  // OBS: mặc định nguồn hiện, chưa phát sóng cho tới khi có tín hiệu.
  const obsRef = useRef({ present, streaming: false, visible: true });

  // Nền trong suốt thật: html, body và #root (body có class bg-white/dark:bg-black).
  useEffect(() => {
    const els = [document.documentElement, document.body, document.getElementById('root')].filter(
      (e): e is HTMLElement => !!e
    );
    const prev = els.map((e) => e.style.getPropertyValue('background'));
    const prevPri = els.map((e) => e.style.getPropertyPriority('background'));
    els.forEach((e) => e.style.setProperty('background', 'transparent', 'important'));
    return () => {
      els.forEach((e, i) => {
        if (prev[i]) e.style.setProperty('background', prev[i], prevPri[i]);
        else e.style.removeProperty('background');
      });
    };
  }, []);

  const apply = useCallback((data: BrandLiveOverlay) => {
    setOverlay(data);
    setIndex((i) => (i >= data.ads.length ? 0 : i));
  }, []);

  // Khoá bị đổi hoặc vị trí bị tắt (401/403/404): bỏ danh sách cũ. Lỗi mạng thì giữ.
  const onError = useCallback((e: unknown) => {
    const st = (e as { response?: { status?: number } })?.response?.status;
    if (st === 401 || st === 403 || st === 404) setOverlay(null);
  }, []);

  // Tải khi mở và mỗi 60 giây; lỗi mạng giữ danh sách cũ.
  useEffect(() => {
    if (!slotId || !key) return undefined;
    let alive = true;
    const load = () =>
      getBrandLiveOverlay(slotId, key)
        .then((d) => alive && apply(d))
        .catch(onError);
    load();
    const t = window.setInterval(load, TICK_MS);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, [slotId, key, apply, onError]);

  // Tín hiệu OBS.
  useEffect(() => {
    const obs = obsRef.current;
    obs.present = !!window.obsstudio;
    window.obsstudio?.getStatus?.((s) => {
      obs.streaming = !!s?.streaming;
    });
    const onStart = () => {
      obs.streaming = true;
    };
    const onStop = () => {
      obs.streaming = false;
    };
    const onVisible = (e: Event) => {
      obs.visible = !!(e as CustomEvent<{ visible?: boolean }>).detail?.visible;
    };
    window.addEventListener('obsStreamingStarted', onStart);
    window.addEventListener('obsStreamingStopped', onStop);
    window.addEventListener('obsSourceVisibleChanged', onVisible);
    return () => {
      window.removeEventListener('obsStreamingStarted', onStart);
      window.removeEventListener('obsStreamingStopped', onStop);
      window.removeEventListener('obsSourceVisibleChanged', onVisible);
    };
  }, []);

  // Nhịp tính giờ mỗi 60 giây.
  useEffect(() => {
    if (!slotId || !key) return undefined;
    let alive = true;
    const t = window.setInterval(() => {
      if (!shouldBeat(obsRef.current)) return;
      sendBrandLiveBeat(slotId, key)
        .then((d) => alive && apply(d))
        .catch(onError);
    }, TICK_MS);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, [slotId, key, apply, onError]);

  // Xoay thẻ.
  const count = overlay?.ads.length ?? 0;
  const rotateMs = Math.max(5, overlay?.rotateSeconds ?? 30) * 1000;
  useEffect(() => {
    if (count < 2) return undefined;
    const t = window.setInterval(() => setIndex((i) => nextRotationIndex(i, count)), rotateMs);
    return () => window.clearInterval(t);
  }, [count, rotateMs]);

  if (!overlay || count === 0) return null;
  const ad = overlay.ads[Math.min(index, count - 1)];

  return (
    <>
      <div style={POSITION[overlay.shape] ?? POSITION.Corner}>
        <BrandLiveCard key={ad.dealId} ad={ad} shape={overlay.shape} />
        {!present && (
          <div
            style={{
              marginTop: 4,
              fontSize: 11,
              color: '#fff',
              textAlign: 'center',
              textShadow: '0 1px 3px rgba(0,0,0,.9)',
            }}
          >
            Đang xem ngoài OBS, không tính giờ
          </div>
        )}
      </div>
    </>
  );
};

export default PageAdLivestream;
